import { useEffect } from 'react';

type CloseOnScrollProps = {
  open: boolean;
  close: () => void;
};

/**
 * Utility component that closes a dropdown/popover when the user scrolls.
 * Uses capture phase to catch scroll events from any scrollable ancestor.
 *
 * @example
 * <Menu>
 *   {({ open, close }) => (
 *     <>
 *       <CloseOnScroll open={open} close={close} />
 *       <MenuButton>...</MenuButton>
 *       <MenuItems>...</MenuItems>
 *     </>
 *   )}
 * </Menu>
 */
export const CloseOnScroll = ({ open, close }: CloseOnScrollProps) => {
  useEffect(() => {
    if (!open) return;
    // Delay attaching the listener slightly so layout-triggered scroll events
    // from the menu opening (e.g. Floating UI positioning) don't fire it immediately.
    const timer = setTimeout(() => {
      window.addEventListener('scroll', close, {
        capture: true,
        passive: true,
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', close, { capture: true });
    };
  }, [open, close]);
  return null;
};
